import { Component, type ErrorInfo, type ReactNode } from 'react';

const KEY = 'ed-reload-after-update';
const isOutdatedFile = (error: unknown) => /dynamically imported module|module script|preload/i.test(error instanceof Error ? error.message : String(error));

/** Une seule tentative par minute : un fichier réellement absent ne doit pas faire recharger la page sans fin. */
function reloadOnce() {
  try {
    const last = Number(sessionStorage.getItem(KEY) ?? 0);
    if (Date.now() - last < 60_000) return false;
    sessionStorage.setItem(KEY, String(Date.now()));
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

/**
 * Remplace la page blanche. Après une mise à jour, un onglet resté ouvert réclame des fichiers qui n'existent plus :
 * la page se recharge alors d'elle-même.
 */
export class AppErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean; reloading: boolean }> {
  override state = { failed: false, reloading: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error(error, info.componentStack);
    if (isOutdatedFile(error) && reloadOnce()) this.setState({ reloading: true });
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    if (this.state.reloading) return <p className="p-8 text-center text-sm text-muted">Mise à jour de l'application…</p>;
    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <h1 className="text-xl font-bold text-navy-900">Cette page n'a pas pu s'afficher</h1>
        <p className="mt-2 text-sm text-muted">Vos données enregistrées ne sont pas touchées. Rechargez la page. Si le message revient, écrivez à expertdevisbtp@gmail.com.</p>
        <button type="button" className="btn-primary mt-4" onClick={() => window.location.reload()}>Recharger la page</button>
      </div>
    );
  }
}
